import { CloudStorageType, EtlInstanceStatus, EtlInstanceType, FileType, } from "@biggeo/bg-server-lib/studio";
import * as A from "fp-ts/Array";
import * as O from "fp-ts/Option";
import { pipe } from "fp-ts/function";
import concat from "lodash/concat";
import debounce from "lodash/debounce";
import difference from "lodash/difference";
import includes from "lodash/includes";
import isEmpty from "lodash/isEmpty";
import isEqual from "lodash/isEqual";
import join from "lodash/join";
import replace from "lodash/replace";
import { thousandsSeparator } from "./input-parse";
import { toNonReadonlyArray } from "./type-helpers";
import { match } from "ts-pattern";
export const formatPhoneNumber = (phone) => {
    const phoneNumber = phone.substring(phone.length - 10);
    const countryCode = phone.substring(0, phone.length - 10);
    const formattedPhoneNumber = `(${phoneNumber.substring(0, 3)}) ${phoneNumber.substring(3, 6)}-${phoneNumber.substring(6)}`;
    return isEmpty(countryCode)
        ? formattedPhoneNumber
        : `+${countryCode} (${phoneNumber.substring(0, 3)}) ${phoneNumber.substring(3, 6)}-${phoneNumber.substring(6)}`;
};
export const updateSelected = (b, a) => {
    return pipe(includes(a, b), O.fromPredicate((x) => x), O.fold(() => concat(a, b), () => a.filter((v) => !isEqual(v, b))));
};
export const debouncedFn = () => {
    return debounce((fn, val) => pipe(val, fn), 300);
};
export const compareIdArrays = (inputIds, existedIds) => {
    const idsToAdd = difference(inputIds, existedIds);
    const idsToDelete = difference(existedIds, inputIds);
    return {
        idsToAdd,
        idsToDelete,
    };
};
export const formatFileSize = (fileSizeInBytes) => {
    if (fileSizeInBytes === 0)
        return "0 B";
    const units = ["B", "KB", "MB", "GB", "TB", "PB", "EB"];
    const threshold = 1024;
    let i = 0;
    let size = fileSizeInBytes;
    while (size >= threshold && i < units.length - 1) {
        size /= threshold;
        i++;
    }
    return `${size.toFixed(1)}${units[i]}`;
};
const validatePath = (path) => {
    return replace(path, /[^a-zA-Z0-9._/-]/g, "");
};
export const sanitizePath = (path) => {
    return typeof path === "string"
        ? validatePath(path)
        : pipe(path, toNonReadonlyArray, A.map(validatePath), (paths) => join(paths, " "));
};
export const isAwsConnectionInfo = (info) => info.cloudStorageType === CloudStorageType.Aws;
export const isAzureConnectionInfo = (info) => info.cloudStorageType === CloudStorageType.Azure;
export const isSfConnectionInfo = (info) => info.cloudStorageType === CloudStorageType.Sf;
export const isAzureStorage = (info) => info.cloudStorageType === CloudStorageType.Azure;
export const isAwsStorage = (info) => info.cloudStorageType === CloudStorageType.Aws;
export const isSfStorage = (info) => info.cloudStorageType === CloudStorageType.Sf;
export const isGcpStorage = (info) => info.cloudStorageType === CloudStorageType.Gcp;
export const isUploadStorage = (info) => info.cloudStorageType === CloudStorageType.Upload;
export const formatNumberWithCommas = (number) => {
    const numString = number.toString();
    const parts = numString.split(".");
    const integerPart = parts[0] ?? "";
    parts[0] = integerPart.replace(/\B(?=(\d{3})+(?!\d))/g, ",");
    return parts.join(".");
};
export const getVelocityUrlOnLocal = (serverUrl) => serverUrl.replace(/\/[^/:]+:/, "/0.0.0.0:");
export const isServiceSAS = (value) => value.includes("sr=b");
export const slugGenerator = (str, allowEndingDash = false) => {
    const removals = new RegExp(/[',",`,~,^,*,$,,,!,@|,%,(,),+,=,\\,\/,.,:,;,<,>,\[,\],\},\{,\?]/g);
    const and = new RegExp(/&/g);
    const space = new RegExp(/ /g);
    const moreSpaces = new RegExp(/ +/g);
    const underscore = new RegExp(/_/g);
    if (allowEndingDash) {
        return str
            .replace(removals, "")
            .replace(and, " and ")
            .replace(moreSpaces, " ")
            .replace(space, "-")
            .replace(underscore, "-")
            .replace(/-{2,}/g, "-")
            .replace(/^-/, "")
            .toLowerCase();
    }
    return str
        .replace(removals, "")
        .replace(and, " and ")
        .replace(moreSpaces, " ")
        .replace(space, "-")
        .replace(underscore, "-")
        .replace(/-{2,}/g, "-")
        .replace(/^-/, "")
        .replace(/-$/, "")
        .toLowerCase();
};
export const formatNumberWithPrecision = (num) => {
    if (num < 0) {
        return 0;
    }
    if (num > 0 && num < 1) {
        return num.toPrecision(2);
    }
    const formatted = num.toFixed(2);
    return pipe(formatted, O.fromPredicate((formatted) => formatted.endsWith(".00")), O.fold(() => formatted, (value) => Number.parseInt(value, 10).toString()), thousandsSeparator);
};
export const toPercentage = (used, total) => {
    if (typeof total === "string") {
        return 0;
    }
    return pipe((used / Number(total)) * 100, O.fromPredicate((x) => !Number.isNaN(x)), O.getOrElse(() => 0), (x) => x.toFixed(2));
};
export const getRandomColor = () => `#${Math.floor(Math.random() * 16777215)
    .toString(16)
    .padStart(6, "0")}`;
export const getFilenameAfterLastSlash = (blobName) => {
    const lastSlashIndex = blobName ? blobName.lastIndexOf("/") : -1;
    if (lastSlashIndex === -1) {
        return blobName;
    }
    return blobName?.substring(lastSlashIndex + 1);
};
export const determineFileType = (paths) => {
    if (paths.every((path) => path.endsWith(FileType.csv))) {
        return FileType.csv;
    }
    if (paths.every((path) => path.endsWith(FileType.parquet))) {
        return FileType.parquet;
    }
    return FileType.unsupported;
};
export const isSuccessfulFileAcquisition = (latestEtlInstance) => latestEtlInstance?.status === EtlInstanceStatus.Completed &&
    latestEtlInstance?.type === EtlInstanceType.FilesAcquisition;
export const isSuccessfulIndexData = (latestEtlInstance) => latestEtlInstance?.status === EtlInstanceStatus.Completed &&
    latestEtlInstance?.type === EtlInstanceType.IndexDataset;
export const splitCamelCase = (text) => {
    return text.replace(/([a-z])([A-Z])/g, "$1 $2");
};
export const filterByPartialMatch = (query, items) => {
    const lowerQuery = query.toLowerCase();
    return items.filter((item) => item.toLowerCase().includes(lowerQuery));
};
export const getSlug = (value) => value
    .toLowerCase()
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/^-+|-+$/g, "")
    .replace(/-{2,}/g, "-");
export const getPreviousAndNextElements = (element, array) => {
    if (!array || isEmpty(array)) {
        return { previousElement: undefined, nextElement: undefined };
    }
    const index = array.indexOf(element);
    const isFirstElement = isEqual(index, 0);
    const isLastElement = isEqual(index, array.length - 1);
    const previousIndex = (array.length + index - 1) % array.length;
    const previousElement = array[previousIndex];
    const nextIndex = (index + 1) % array.length;
    const nextElement = array[nextIndex];
    return {
        previousElement: isFirstElement ? undefined : previousElement,
        nextElement: isLastElement ? undefined : nextElement,
    };
};
export const getFileTypeLabel = (fileType) => match(fileType)
    .with(FileType.csv, () => "CSV")
    .with(FileType.parquet, () => "Parquet")
    .with(FileType.shp, () => "Shapefile")
    .with(FileType.unsupported, () => "Unsupported File Type")
    .exhaustive();
export const singularOrPlural = (count, singular, plural) => {
    const formattedCount = count
        .toString()
        .replace(/\B(?=(\d{3})+(?!\d))/g, ",");
    return `${formattedCount} ${count === 1 ? singular : (plural ?? `${singular}s`)}`;
};
export const isPurchasedPermission = (dataSourcePermission) => {
    return (!!dataSourcePermission.fkUserId &&
        !!dataSourcePermission.fkDataSourcePriceId);
};
export const isAccessKeyPermission = (dataSourcePermission) => {
    return (!!dataSourcePermission.fkUserId &&
        !dataSourcePermission.fkDataSourcePriceId &&
        !dataSourcePermission.sampleId);
};
export const isSamplePermission = (dataSourcePermission) => {
    return (!!dataSourcePermission.fkUserId &&
        !dataSourcePermission.fkDataSourcePriceId &&
        !!dataSourcePermission.sampleId);
};
//# sourceMappingURL=helpers.js.map